import { EnumDialogName } from '@calm/evexia-analytics';
import { Heading } from '@rhg/components/heading';
import { Stack, SxProps, Theme, TypographyProps } from '@rhg/libs/material';
import SimpleDialog from '@rhg/libs/material/simple-dialog';
import { sxAppend } from '@rhg/libs/material/sx';
import dynamic from 'next/dynamic';
import { useId } from 'react';
import { LoadingDialogOptions } from '.';

const LoadingDots = dynamic(() =>
    import('@rhg/components/animations/loading-dots').then(
        (mod) => mod.LoadingDots
    )
);

interface LoadingDialogProps {
    isOpen: boolean;
    options: Omit<LoadingDialogOptions, 'timeout'>;
}
interface LoadingDialogContentProps {
    title?: string;
    subtitle?: string;
    titleVariant?: TypographyProps['variant'];
    titleId?: string;
    boxStyles?: SxProps<Theme>;
    headingStyles?: SxProps<Theme>;
    isReady?: boolean;
}

const styleLoadingDialog = {
    zIndex: (theme: Theme) => theme.zIndex.modal + 1,
};

const styleDialogPaper = {
    backgroundColor: (theme: Theme) => theme.palette.background.paper,
};

const styleContentHeading = {
    mt: 4,
    pt: 2,
};

const styleContentBox = { paddingBottom: 16.5 };

const styleDialogContent = {
    height: '100%',
};

const styleNewLines = {
    whiteSpace: 'pre-line',
};

export const LoadingDialogContent = ({
    titleId,
    title,
    subtitle,
    titleVariant = 'h4',
    boxStyles,
    headingStyles,
    isReady = true,
}: LoadingDialogContentProps) => {
    // Lazily load loading dots since lottie is huge
    return (
        <Stack
            direction="column"
            alignItems="center"
            sx={sxAppend(styleDialogContent, boxStyles)}
        >
            {isReady && <LoadingDots />}
            <Heading
                title={title}
                titleAlign="center"
                titleVariant={titleVariant}
                titleComponent="h2"
                titleId={titleId}
                subtitle={subtitle}
                subtitleAlign="center"
                subtitleVariant="body1"
                subtitleStyle={styleNewLines}
                sx={headingStyles}
            />
        </Stack>
    );
};

export const LoadingDialog = ({ isOpen, options }: LoadingDialogProps) => {
    const id = useId();

    return (
        <SimpleDialog
            PaperProps={{ sx: styleDialogPaper }}
            trackDialog={options?.trackDialog || EnumDialogName.Toast}
            isOpen={isOpen}
            onClose={() => options?.onClose && options.onClose()}
            aria-labelledby={id}
            hideCloseButton
            hasGradientBackground={!!options?.hasGradientBackground}
            fullScreen
            disableBackdropClick
            disableEscapeKeyDown
            sx={styleLoadingDialog}
            transitionDuration={options?.transitionDuration}
            content={
                <LoadingDialogContent
                    isReady={isOpen}
                    title={options?.title}
                    titleId={id}
                    subtitle={options?.subtitle}
                    boxStyles={styleContentBox}
                    headingStyles={styleContentHeading}
                />
            }
        />
    );
};
